/**
 * @fileoverview useAutoLogout.js v2 - Funciona con pestañas en segundo plano
 * 
 * SOLUCIÓN: Los navegadores pausan timers en pestañas inactivas.
 * Usamos localStorage para guardar timestamps y verificar al volver.
 * 
 * Funcionalidad:
 * - Guarda timestamp de última actividad en localStorage
 * - Cuando el usuario vuelve a la pestaña, verifica cuánto tiempo pasó
 * - Muestra advertencia 1 minuto antes de cerrar (solo si está activo)
 * - Cierra sesión si pasaron más de 1 hora
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Configuración de tiempos (en milisegundos)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutos (1 hora)
const WARNING_BEFORE = 1 * 60 * 1000;      // Mostrar advertencia 1 minuto antes
const WARNING_TIMEOUT = INACTIVITY_TIMEOUT - WARNING_BEFORE; // 59 minutos
const STORAGE_KEY = 'lastActivityTimestamp';

export default function useAutoLogout() {
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);
  
  // Referencias para los timers (solo funcionan cuando la pestaña está activa)
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  // Función para obtener el timestamp de última actividad
  const getLastActivity = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  }, []);

  // Función para guardar el timestamp de última actividad
  const setLastActivity = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  }, []);

  // Función para limpiar todos los timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Función para hacer logout
  const performLogout = useCallback(() => {
    // Limpiar todos los timers
    clearAllTimers();

    // Limpiar localStorage
    localStorage.removeItem('nfcToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('pinBypass');
    localStorage.removeItem('authToken');
    localStorage.removeItem(STORAGE_KEY);

    // ✅ Limpiar sessionStorage (PIN validado)
    sessionStorage.removeItem('pinValidated');
    sessionStorage.removeItem('pinBypassed');

    // Cerrar modal de advertencia
    setShowWarning(false);
    
    // Mostrar mensaje
    toast.info('Tu sesión se cerró por inactividad', {
      autoClose: 5000,
      position: 'top-center'
    });
    
    // Redirigir al inicio
    navigate('/');
  }, [navigate, clearAllTimers]);

  // Función para mostrar la advertencia
  const showWarningModal = useCallback((remainingSeconds = 60) => {
    setShowWarning(true);
    setSecondsRemaining(remainingSeconds);
    
    // Limpiar countdown anterior si existe
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    
    // Iniciar countdown
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          performLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [performLogout]);

  // Función para verificar si debe hacer logout basado en timestamp
  const checkInactivity = useCallback(() => {
    const lastActivity = getLastActivity();
    const timePassed = Date.now() - lastActivity;
    
    if (timePassed >= INACTIVITY_TIMEOUT) {
      // Ya pasó el tiempo límite, hacer logout inmediato
      performLogout();
      return true;
    } else if (timePassed >= WARNING_TIMEOUT) {
      // Ya pasó el tiempo de advertencia, mostrar modal
      const remainingMs = INACTIVITY_TIMEOUT - timePassed;
      const remainingSecs = Math.max(1, Math.ceil(remainingMs / 1000));
      showWarningModal(remainingSecs);
      return false;
    }
    
    return false;
  }, [getLastActivity, performLogout, showWarningModal]);

  // Función para reiniciar los timers (cuando hay actividad)
  const resetTimers = useCallback(() => {
    // Guardar timestamp actual
    setLastActivity();
    
    // Limpiar timers existentes
    clearAllTimers();
    
    // Cerrar modal de advertencia si está abierto
    setShowWarning(false);
    setSecondsRemaining(60);
    
    // Programar nueva advertencia (solo funciona si la pestaña está activa)
    warningTimerRef.current = setTimeout(() => {
      // Verificar de nuevo antes de mostrar
      const lastActivity = getLastActivity();
      const timePassed = Date.now() - lastActivity;
      
      if (timePassed >= WARNING_TIMEOUT) {
        const remainingMs = INACTIVITY_TIMEOUT - timePassed;
        const remainingSecs = Math.max(1, Math.ceil(remainingMs / 1000));
        showWarningModal(remainingSecs);
      }
    }, WARNING_TIMEOUT);
  }, [clearAllTimers, setLastActivity, getLastActivity, showWarningModal]);

  // Función para extender la sesión (cuando el usuario hace clic en "Seguir conectado")
  const extendSession = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    resetTimers();
    toast.success('Sesión extendida', { autoClose: 2000 });
  }, [resetTimers, clearAllTimers]);

  // Función para cerrar sesión manualmente desde el modal
  const logoutNow = useCallback(() => {
    performLogout();
  }, [performLogout]);

  // Efecto principal: configurar event listeners
  useEffect(() => {
    // Inicializar timestamp si no existe
    if (!localStorage.getItem(STORAGE_KEY)) {
      setLastActivity();
    }

    // Eventos que indican actividad del usuario
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];

    // Handler para eventos de actividad (throttled)
    let lastUpdate = 0;
    const handleActivity = () => {
      const now = Date.now();
      // Solo actualizar cada 5 segundos para no sobrecargar
      if (now - lastUpdate > 5000 && !showWarning) {
        lastUpdate = now;
        setLastActivity();
      }
    };

    // Handler para cambio de visibilidad (CLAVE: esto SÍ se ejecuta al volver)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Usuario cambió de pestaña/app - guardar timestamp
      } else {
        // Usuario volvió - VERIFICAR CUÁNTO TIEMPO PASÓ
        // Pequeño delay para asegurar que todo esté listo
        setTimeout(() => {
          if (!showWarning) {
            const shouldLogout = checkInactivity();
            if (!shouldLogout) {
              // No excedió el tiempo, reiniciar timers
              resetTimers();
            }
          }
        }, 100);
      }
    };

    // Handler para cuando la ventana gana foco
    const handleFocus = () => {
      setTimeout(() => {
        if (!showWarning) {
          const shouldLogout = checkInactivity();
          if (!shouldLogout) {
            resetTimers();
          }
        }
      }, 100);
    };

    // Agregar event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Iniciar el primer timer
    resetTimers();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      clearAllTimers();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  // Verificación periódica (cada 30 segundos) como respaldo
  useEffect(() => {
    const intervalCheck = setInterval(() => {
      // Solo verificar si la pestaña está visible y no hay advertencia
      if (!document.hidden && !showWarning) {
        const lastActivity = getLastActivity();
        const timePassed = Date.now() - lastActivity;

        if (timePassed >= WARNING_TIMEOUT) {
          checkInactivity();
        }
      }
    }, 30000); // Cada 30 segundos

    return () => clearInterval(intervalCheck);
  }, [getLastActivity, checkInactivity, showWarning]);

  return {
    showWarning,
    secondsRemaining,
    extendSession,
    logoutNow
  };
}